import { formatDate, getPlanLabel } from './utils'

export const PLAN_RANKS = {
  free: 0,
  starter: 1,
  pro: 2,
  business: 3,
  enterprise: 4,
}

function normalizeCommercialPlan(plan) {
  const normalized = String(plan || '').trim().toLowerCase()
  return ['free', 'starter', 'pro', 'business', 'enterprise'].includes(normalized)
    ? normalized
    : null
}

export function getSubscriptionPlan({ user, org, usage } = {}) {
  if (user?.isPlatformAdmin) return 'admin'
  if (isManualPlanOverride({ user, org, usage })) {
    return normalizeCommercialPlan(
      org?.manualPlanOverridePlan ||
      org?.billing?.manualPlanOverridePlan ||
      usage?.manualPlanOverridePlan ||
      user?.manualPlanOverridePlan ||
      org?.plan ||
      user?.plan
    ) || 'free'
  }
  return normalizeCommercialPlan(
    org?.subscriptionPlan ||
    org?.billing?.plan ||
    usage?.subscriptionPlan ||
    usage?.plan ||
    org?.plan ||
    user?.plan
  ) || 'free'
}

export function getEffectivePlan({ user, org, usage } = {}) {
  if (user?.isPlatformAdmin) return 'admin'
  return getSubscriptionPlan({ user, org, usage })
}

export function getSubscriptionStatus({ user, org, usage } = {}) {
  if (user?.isPlatformAdmin) return null
  if (isManualPlanOverride({ user, org, usage })) return null
  return (
    org?.subscriptionStatus ||
    org?.billing?.subscriptionStatus ||
    usage?.subscriptionStatus ||
    null
  )
}

export function getCurrentPeriodEnd({ user, org, usage } = {}) {
  if (user?.isPlatformAdmin) return null
  if (isManualPlanOverride({ user, org, usage })) return null
  return (
    org?.currentPeriodEnd ||
    org?.billing?.currentPeriodEnd ||
    usage?.currentPeriodEnd ||
    null
  )
}

export function getCancelAtPeriodEnd({ user, org, usage } = {}) {
  if (user?.isPlatformAdmin) return false
  if (isManualPlanOverride({ user, org, usage })) return false
  return Boolean(
    org?.cancelAtPeriodEnd ||
    org?.billing?.cancelAtPeriodEnd ||
    usage?.cancelAtPeriodEnd
  )
}

export function formatBillingPlanSummary(
  plan,
  {
    cancelAtPeriodEnd = false,
    currentPeriodEnd = null,
    includePlanSuffix = true,
  } = {}
) {
  const planLabel = includePlanSuffix
    ? `${getPlanLabel(plan)} plan`
    : getPlanLabel(plan)
  if (cancelAtPeriodEnd && currentPeriodEnd) {
    return `${planLabel} \u2014 cancels on ${formatDate(currentPeriodEnd)}`
  }
  if (cancelAtPeriodEnd) {
    return `${planLabel} \u2014 cancels at period end`
  }
  return planLabel
}

export function formatScheduledCancellationMessage(plan, currentPeriodEnd) {
  const planLabel = `${getPlanLabel(plan)} plan`
  if (currentPeriodEnd) {
    return `Your ${planLabel} remains active until ${formatDate(currentPeriodEnd)}. It is scheduled to cancel.`
  }
  return `Your ${planLabel} remains active until the end of the current billing period. It is scheduled to cancel.`
}

export const BILLING_PORTAL_CANCELLATION_NOTE =
  'Stripe may still show "Renew subscription" or "Don\'t cancel subscription" because the subscription remains active until the billing period ends. Use the cancellation status shown here as the source of truth.'

export function getPlanRank(plan) {
  return PLAN_RANKS[String(plan || '').trim().toLowerCase()] ?? -1
}

export function hasManagedBilling({ org, usage } = {}) {
  if (isManualPlanOverride({ org, usage })) return false
  return Boolean(
    org?.stripeCustomerId ||
    org?.stripeSubscriptionId ||
    org?.subscriptionStatus ||
    usage?.subscriptionStatus
  )
}

export function isPaidPlan(plan) {
  return ['starter', 'pro', 'business', 'enterprise'].includes(plan)
}

export function isManualPlanOverride({ user, org, usage } = {}) {
  return Boolean(
    user?.manualPlanOverride ||
    user?.billingSource === 'manual_override' ||
    org?.manualPlanOverride ||
    org?.billingSource === 'manual_override' ||
    org?.billing?.manualPlanOverride ||
    org?.billing?.billingSource === 'manual_override' ||
    usage?.manualPlanOverride
    || usage?.billingSource === 'manual_override'
  )
}
